import { subscriptionAtom } from '@/atoms/subscription.atom';
import { userAtom } from '@/atoms/user.atom';
import { SubscriptionInfo } from '@/graphql/graphql';
import { FEATURE_GATES, FeatureGate, FeatureKey, isFeatureAllowed } from '@/lib/featureGates';
import { useDialog } from '@/providers/DialogProvider';
import { useAtomValue } from 'jotai';
import { useCallback, useMemo } from 'react';
import { UpgradeDialog } from '@/components/subscription/UpgradeDialog';

export interface FeatureResult {
  allowed: boolean;
  gate: FeatureGate;
  subscription: SubscriptionInfo;
  promptUpgrade: () => void;
}

export const useFeature = (key: FeatureKey): FeatureResult => {
  const subscription = useAtomValue(subscriptionAtom);
  const user = useAtomValue(userAtom);
  const { openDialog } = useDialog();

  const gate = FEATURE_GATES[key];
  const allowed = isFeatureAllowed(subscription, user?.email, key);

  const promptUpgrade = useCallback(() => {
    openDialog(<UpgradeDialog feature={key} />);
  }, [openDialog, key]);

  return useMemo(
    () => ({ allowed, gate, subscription, promptUpgrade }),
    [allowed, gate, subscription, promptUpgrade],
  );
};

// Imperative variant for use inside event handlers / non-render contexts.
// Returns a stable function that evaluates a feature and (optionally) opens the upgrade dialog.
export const useFeatureCheck = () => {
  const subscription = useAtomValue(subscriptionAtom);
  const user = useAtomValue(userAtom);
  const { openDialog } = useDialog();

  return useCallback(
    (key: FeatureKey): FeatureResult => {
      const gate = FEATURE_GATES[key];
      const allowed = isFeatureAllowed(subscription, user?.email, key);
      return {
        allowed,
        gate,
        subscription,
        promptUpgrade: () => openDialog(<UpgradeDialog feature={key} />),
      };
    },
    [subscription, user?.email, openDialog],
  );
};
